import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ButtonVariant = "primary" | "secondary" | "consent" | "danger" | "ghost";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Grosse Variante mit Stadion-Radius, fuer den zentralen CTA einer Ansicht. */
  large?: boolean;
  block?: boolean;
  children: ReactNode;
}

/**
 * Buttons nach DESIGN-mastercard.md Abschnitt 4.
 *
 * `consent` ist bewusst eine eigene Variante und keine Farboption: das Orange
 * signalisiert Einwilligung und Recht. Wird es fuer gewoehnliche Aktionen
 * verwendet, verliert genau diese Bedeutung ihre Wirkung.
 *
 * `type` steht standardmaessig auf "button". Der Browser-Standard "submit"
 * schickt sonst versehentlich das umgebende Formular ab.
 */
export function Button({
  variant = "primary",
  large = false,
  block = false,
  type = "button",
  className,
  children,
  ...rest
}: ButtonProps): React.ReactElement {
  const classes = [
    "btn",
    `btn--${variant}`,
    large ? "btn--large" : null,
    block ? "btn--block" : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button type={type} className={classes} {...rest}>
      {children}
    </button>
  );
}

export interface SatelliteButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Pflicht: der Button enthaelt nur ein Symbol und braucht einen Namen. */
  label: string;
  icon?: ReactNode;
}

/**
 * Runder Mikro-CTA, der an einem Kreisportraet andockt.
 *
 * Weil er ausser einem Pfeil nichts enthaelt, ist `label` nicht optional --
 * ein Button ohne zugaenglichen Namen ist per Screenreader nicht benennbar.
 */
export function SatelliteButton({
  label,
  icon,
  className,
  type = "button",
  ...rest
}: SatelliteButtonProps): React.ReactElement {
  return (
    <button
      type={type}
      className={["satellite", className].filter(Boolean).join(" ")}
      aria-label={label}
      {...rest}
    >
      {icon ?? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
          <path d="M5 12h14" strokeLinecap="round" />
          <path d="m13 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}
